import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  Label,
  MultiSelect,
  RadioGroup,
  RangeSlider,
  SectionTitle,
  Select,
  WhyWeAsk,
} from './FormComponents';

import type { Preferences } from '../../types/application';

export type StepEErrors = Partial<Record<string, string>>;

interface StepEPreferencesProps {
  value: Preferences;
  errors: StepEErrors;
  onChange: (patch: Partial<Preferences>) => void;
}

const LANGUAGES = [
  { value: 'English', label: 'English' },
  { value: 'Spanish', label: 'Spanish' },
  { value: 'Portuguese', label: 'Portuguese' },
  { value: 'French', label: 'French' },
  { value: 'Mandarin', label: 'Mandarin' },
  { value: 'Cantonese', label: 'Cantonese' },
  { value: 'Korean', label: 'Korean' },
  { value: 'Vietnamese', label: 'Vietnamese' },
  { value: 'Tagalog', label: 'Tagalog' },
  { value: 'Creole', label: 'Haitian Creole' },
  { value: 'Russian', label: 'Russian' },
  { value: 'Arabic', label: 'Arabic' },
];

const RACE_OPTIONS = [
  { value: '', label: 'Prefer not to say' },
  { value: 'hispanic', label: 'Hispanic or Latino' },
  { value: 'white', label: 'White' },
  { value: 'black', label: 'Black or African American' },
  { value: 'asian', label: 'Asian' },
  { value: 'native', label: 'American Indian or Alaska Native' },
  { value: 'pacific', label: 'Native Hawaiian or Pacific Islander' },
  { value: 'two-or-more', label: 'Two or More Races' },
];

const GENDER_OPTIONS = [
  { value: '', label: 'Prefer not to say' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non-binary', label: 'Non-binary' },
];

export function StepEPreferences({ value, errors, onChange }: StepEPreferencesProps) {
  const { t } = useTranslation();

  return (
    <div className='space-y-6'>
      <SectionTitle title={t('wizard.stepE.title')} subtitle={t('wizard.stepE.subtitle')} />

      {/* Indoor/Outdoor Comfort */}
      <div>
        <Label error={errors.indoorOutdoorComfort}>{t('wizard.stepE.indoorOutdoorComfort')}</Label>
        <p className='text-xs text-gray-500 mb-2'>{t('wizard.stepE.indoorOutdoorHint')}</p>
        <RangeSlider
          value={value.indoorOutdoorComfort}
          onChange={(val) => onChange({ indoorOutdoorComfort: val })}
          min={1}
          max={5}
          labels={{
            min: t('wizard.stepE.preferIndoor'),
            max: t('wizard.stepE.preferOutdoor'),
          }}
          error={errors.indoorOutdoorComfort}
          aria-label={t('wizard.stepE.indoorOutdoorComfort')}
        />
      </div>

      {/* Standing/Patrol Tolerance */}
      <div>
        <Label error={errors.standingPatrolTolerance}>{t('wizard.stepE.standingPatrolTolerance')}</Label>
        <p className='text-xs text-gray-500 mb-2'>{t('wizard.stepE.standingPatrolHint')}</p>
        <RangeSlider
          value={value.standingPatrolTolerance}
          onChange={(val) => onChange({ standingPatrolTolerance: val })}
          min={1}
          max={5}
          labels={{
            min: t('wizard.stepE.preferStationary'),
            max: t('wizard.stepE.preferPatrol'),
          }}
          error={errors.standingPatrolTolerance}
          aria-label={t('wizard.stepE.standingPatrolTolerance')}
        />
      </div>

      {/* Languages Spoken */}
      <div>
        <Label required error={errors.languagesSpoken}>
          {t('wizard.stepE.languagesSpoken')}
        </Label>
        <p className='text-xs text-gray-500 mb-2'>{t('wizard.stepE.languagesHint')}</p>
        <MultiSelect
          options={LANGUAGES}
          selected={value.languagesSpoken}
          onChange={(languages) => onChange({ languagesSpoken: languages })}
          error={errors.languagesSpoken}
          columns={4}
        />
      </div>

      {/* Veteran Status */}
      <div>
        <Label>
          {t('wizard.stepE.veteranStatus')}
          <WhyWeAsk text={t('wizard.whyWeAsk.veteranStatus')} />
        </Label>
        <p className='text-xs text-gray-500 mb-2'>{t('wizard.stepE.veteranHint')}</p>
        <RadioGroup
          name='isVeteran'
          value={value.isVeteran}
          options={[
            { value: true, label: t('common.yes') },
            { value: false, label: t('common.no') },
            { value: undefined as unknown as boolean, label: t('wizard.stepE.preferNotToSay') },
          ]}
          onChange={(val) => onChange({ isVeteran: val === undefined ? undefined : (val as boolean) })}
          inline
        />
      </div>

      {/* EEO Survey Section */}
      <div className='border-t pt-6 mt-6'>
        <h3 className='font-semibold text-gray-900 mb-2'>
          {t('wizard.stepE.eeoTitle')}
          <WhyWeAsk text={t('wizard.whyWeAsk.eeo')} />
        </h3>
        <p className='text-xs text-gray-500 mb-4'>{t('wizard.stepE.eeoDisclaimer')}</p>

        <div className='mb-4'>
          <Checkbox
            label={t('wizard.stepE.eeoConsentLabel')}
            checked={value.eeoDisclosureConsent}
            onChange={(checked) => onChange({ eeoDisclosureConsent: checked })}
          />
        </div>

        {value.eeoDisclosureConsent ? (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg'>
            <div>
              <Label>{t('wizard.stepE.eeoRace')}</Label>
              <Select
                value={value.eeoRace ?? ''}
                onChange={(e) => onChange({ eeoRace: e.target.value === '' ? undefined : e.target.value })}>
                {RACE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>{t('wizard.stepE.eeoGender')}</Label>
              <Select
                value={value.eeoGender ?? ''}
                onChange={(e) => onChange({ eeoGender: e.target.value === '' ? undefined : e.target.value })}>
                {GENDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
